import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { filter, finalize, share, takeUntil, tap } from 'rxjs/operators'
import { merge, Observable, Subject } from 'rxjs'

import { AuthService } from '../auth.service'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'

@Component({
  selector: 'lin-set-password',
  templateUrl: './set-password.component.html',
  styleUrls: ['./set-password.component.scss'],
  standalone: false,
})
export class SetPasswordComponent implements OnInit, OnDestroy {
  form: FormGroup
  isSubmitting = false
  isFormShown = false
  showPassword = false
  formError: string
  platformSettings$: Observable<PlatformSettings>

  private setNewPasswordParams: string
  private token: string
  private readonly onDestroy$ = new Subject<void>()

  get isDisabled(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return !!this.form.get('password')?.value?.length
  }

  readonly RANDOM_PASSWORD_LENGTH: number = 8
  readonly CHARS: ReadonlyArray<Record<string, string>> = [
    { smallLetters: 'abcdefghijklmnopqrstuvwxyz' },
    { capitalLetters: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
    { numbers: '0123456789' },
    { specialChars: '!@#$%^&*()_=?/' },
  ]

  constructor(
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly platformSettingsService: PlatformSettingsService,
  ) {}

  ngOnInit(): void {
    this.initForm()
    this.checkResetPasswordToken()
    this.platformSettings$ = this.platformSettingsService.platformSettings$
  }

  private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: unknown } => {
      if (!control.value) {
        return null
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return regex.test(control.value) ? null : error
    }
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      password: [
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          this.patternValidator(/\d/, { hasNumber: true }),
          this.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
          this.patternValidator(/[a-z]/, { hasSmallCase: true }),
          this.patternValidator(/[!@#$%^&*()_=?/]/, {
            hasSpecialCharacters: true,
          }),
        ]),
      ],
    })

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => (this.formError = ''))
  }

  private checkResetPasswordToken(): void {
    this.token = this.route.snapshot.params.token as string
    this.setNewPasswordParams = this.route.snapshot.params.setNewPassword as string

    if (this.setNewPasswordParams) {
      this.isFormShown = true

      return
    }

    const checkResetPasswordToken$: Observable<boolean> = this.authService.checkResetPasswordToken(this.token).pipe(
      finalize(() => (this.isSubmitting = false)),
      share(),
    )

    const validToken$: Observable<boolean> = checkResetPasswordToken$.pipe(
      filter(Boolean),
      tap(() => (this.isFormShown = true)),
    )

    const invalidToken$: Observable<boolean> = checkResetPasswordToken$.pipe(
      filter(negativeBoolean),
      tap(() => void this.router.navigate(['expired-link'])),
    )

    merge(invalidToken$, validToken$)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        (error) => (this.formError = handleFormErrors(this.form)(error)),
      )
  }

  changePassword(): void {
    this.isSubmitting = true
    const formData = this.form.value as { password: string }

    const action$ = this.setNewPasswordParams
      ? this.authService.setNewPassword(this.token, formData.password)
      : this.authService.resetPassword(this.token, formData.password)

    action$
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe(
        () => void this.router.navigate(['login']),
        (error) => (this.formError = handleFormErrors(this.form)(error)),
      )
  }

  generatePassword(): void {
    this.form.controls.password.setValue(this.randomPassword())
    this.form.markAsDirty()
  }

  private randomPassword(): string {
    const password = []

    for (let x = 0; password.length < this.RANDOM_PASSWORD_LENGTH; x++) {
      this.CHARS.map((el) => {
        if (password.length < this.RANDOM_PASSWORD_LENGTH) {
          password.push(this.getRand(Object.values(el)[0]))
        }
      })
    }

    return password.join('')
  }

  private getRand(values: string): string {
    return values.charAt(Math.floor(Math.random() * values.length))
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
